import { Patient, SessionSummary, Subtenant } from '@bioliberty/lifemind';
import { useMemo } from 'react';
import { useTenantIds } from '../../hooks/useTenantIds';
import { MetricPart, UTILIZATION_METRICS, UtilizationMetric } from '../components/utilizationMetrics';

export interface MonthColumn {
  key: string;
  year: number;
  month: number;
  label: string;
}

export interface ClinicRow {
  tenantId: string;
  label: string;
  firstActiveMonth: string | null;
  cells: Map<string, number>;
}

export interface UtilizationData {
  clinics: ClinicRow[];
  months: MonthColumn[];
  totals: Map<string, number>;
}

const EMPTY: UtilizationData = { clinics: [], months: [], totals: new Map() };

export function useUtilizationData(
  sessions: SessionSummary[],
  newPatients: Patient[],
  subtenants: Subtenant[] | undefined,
  metric: UtilizationMetric
): UtilizationData {
  const tenantIds = useTenantIds();

  return useMemo(() => {
    if ((!sessions.length && !newPatients.length) || !subtenants?.length) {
      return EMPTY;
    }

    const config = UTILIZATION_METRICS[metric];
    const numerator = aggregatePart(sessions, newPatients, config.numerator);
    const denominator = config.denominator ? aggregatePart(sessions, newPatients, config.denominator) : null;

    if (!numerator.earliestDate) {
      return EMPTY;
    }

    const clinicCells = combineClinicCells(numerator.clinicCells, denominator?.clinicCells);
    const rawTotals = combineTotals(numerator.totals, denominator?.totals);

    const months = generateMonthRange(numerator.earliestDate, new Date());

    const sortedSubtenants = [...subtenants]
      .filter(({ name }) => tenantIds?.includes(name))
      .sort((a, b) => a.id - b.id);

    const clinics = buildClinicRows(sortedSubtenants, months, clinicCells);
    const filledTotals = buildTotals(months, rawTotals);

    return { clinics, months, totals: filledTotals };
  }, [sessions, newPatients, subtenants, tenantIds, metric]);
}

interface AggregatedData {
  clinicCells: Map<string, Map<string, number>>;
  totals: Map<string, number>;
  earliestDate: Date | null;
}

function aggregatePart(sessions: SessionSummary[], newPatients: Patient[], part: MetricPart): AggregatedData {
  if (part === 'distinctPatients') {
    return aggregateDistinctPatients(sessions);
  }
  if (part === 'newPatients') {
    return aggregateNewPatients(newPatients);
  }
  if (part === 'sessionCount') {
    return aggregateNumeric(sessions, () => 1);
  }
  return aggregateNumeric(sessions, (session) => session[part.sum]);
}

function aggregateDistinctPatients(sessions: SessionSummary[]): AggregatedData {
  const clinicMonthPatients = new Map<string, Map<string, Set<number>>>();
  const monthAllPatients = new Map<string, Set<number>>();
  let earliestDate: Date | null = null;

  for (const session of sessions) {
    const date = new Date(session.startedOn);
    const monthKey = toMonthKey(date);

    if (!earliestDate || date < earliestDate) {
      earliestDate = date;
    }

    let clinicMonths = clinicMonthPatients.get(session.tenantId);
    if (!clinicMonths) {
      clinicMonths = new Map();
      clinicMonthPatients.set(session.tenantId, clinicMonths);
    }

    let patients = clinicMonths.get(monthKey);
    if (!patients) {
      patients = new Set();
      clinicMonths.set(monthKey, patients);
    }
    patients.add(session.patientId);

    let allPatients = monthAllPatients.get(monthKey);
    if (!allPatients) {
      allPatients = new Set();
      monthAllPatients.set(monthKey, allPatients);
    }
    allPatients.add(session.patientId);
  }

  const clinicCells = new Map<string, Map<string, number>>();
  for (const [tenantId, monthMap] of clinicMonthPatients) {
    const cells = new Map<string, number>();
    for (const [monthKey, set] of monthMap) {
      cells.set(monthKey, set.size);
    }
    clinicCells.set(tenantId, cells);
  }

  const totals = new Map<string, number>();
  for (const [monthKey, set] of monthAllPatients) {
    totals.set(monthKey, set.size);
  }

  return { clinicCells, totals, earliestDate };
}

function aggregateNewPatients(newPatients: Patient[]): AggregatedData {
  const clinicCells = new Map<string, Map<string, number>>();
  const totals = new Map<string, number>();
  let earliestDate: Date | null = null;

  for (const patient of newPatients) {
    if (!patient.firstActivityDate) {
      continue;
    }

    const date = new Date(patient.firstActivityDate);
    const monthKey = toMonthKey(date);

    if (!earliestDate || date < earliestDate) {
      earliestDate = date;
    }

    let clinicMonths = clinicCells.get(patient.tenantId);
    if (!clinicMonths) {
      clinicMonths = new Map();
      clinicCells.set(patient.tenantId, clinicMonths);
    }
    clinicMonths.set(monthKey, (clinicMonths.get(monthKey) ?? 0) + 1);

    totals.set(monthKey, (totals.get(monthKey) ?? 0) + 1);
  }

  return { clinicCells, totals, earliestDate };
}

function aggregateNumeric(sessions: SessionSummary[], getValue: (session: SessionSummary) => number): AggregatedData {
  const clinicCells = new Map<string, Map<string, number>>();
  const totals = new Map<string, number>();
  let earliestDate: Date | null = null;

  for (const session of sessions) {
    const date = new Date(session.startedOn);
    const monthKey = toMonthKey(date);
    const value = getValue(session);

    if (!earliestDate || date < earliestDate) {
      earliestDate = date;
    }

    let clinicMonths = clinicCells.get(session.tenantId);
    if (!clinicMonths) {
      clinicMonths = new Map();
      clinicCells.set(session.tenantId, clinicMonths);
    }
    clinicMonths.set(monthKey, (clinicMonths.get(monthKey) ?? 0) + value);

    totals.set(monthKey, (totals.get(monthKey) ?? 0) + value);
  }

  return { clinicCells, totals, earliestDate };
}

function combineClinicCells(
  numerator: Map<string, Map<string, number>>,
  denominator: Map<string, Map<string, number>> | undefined
): Map<string, Map<string, number>> {
  if (!denominator) {
    return numerator;
  }
  const result = new Map<string, Map<string, number>>();
  for (const [tenantId, numMonths] of numerator) {
    const denomMonths = denominator.get(tenantId);
    const cells = new Map<string, number>();
    for (const [monthKey, numValue] of numMonths) {
      const denomValue = denomMonths?.get(monthKey) ?? 0;
      cells.set(monthKey, denomValue > 0 ? numValue / denomValue : 0);
    }
    result.set(tenantId, cells);
  }
  return result;
}

function combineTotals(
  numerator: Map<string, number>,
  denominator: Map<string, number> | undefined
): Map<string, number> {
  if (!denominator) {
    return numerator;
  }
  const result = new Map<string, number>();
  for (const [monthKey, numValue] of numerator) {
    const denomValue = denominator.get(monthKey) ?? 0;
    result.set(monthKey, denomValue > 0 ? numValue / denomValue : 0);
  }
  return result;
}

function buildClinicRows(
  subtenants: Subtenant[],
  months: MonthColumn[],
  clinicCells: Map<string, Map<string, number>>
): ClinicRow[] {
  return subtenants.map((subtenant) => {
    const cellSource = clinicCells.get(subtenant.name);
    const cells = new Map<string, number>();

    let firstActiveMonth: string | null = null;
    for (const month of months) {
      const value = cellSource?.get(month.key) ?? 0;
      cells.set(month.key, value);

      if (value > 0 && !firstActiveMonth) {
        firstActiveMonth = month.key;
      }
    }

    return {
      tenantId: subtenant.name,
      label: subtenant.label,
      firstActiveMonth,
      cells
    };
  });
}

function buildTotals(months: MonthColumn[], rawTotals: Map<string, number>): Map<string, number> {
  const totals = new Map<string, number>();
  for (const month of months) {
    totals.set(month.key, rawTotals.get(month.key) ?? 0);
  }
  return totals;
}

function toMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;
}

function generateMonthRange(earliest: Date, latest: Date): MonthColumn[] {
  const months: MonthColumn[] = [];
  const current = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
  const end = new Date(latest.getFullYear(), latest.getMonth(), 1);

  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();
    months.push({
      key: toMonthKey(current),
      year,
      month,
      label: current.toLocaleString('en-US', { month: 'short', year: 'numeric' })
    });
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}
